<script lang="ts">
  import { Alert } from "@getprovi/craft-svelte";
  import ScheduledDelivery from "@shared/svelte/Components/DeliveryDate/ScheduledDelivery.svelte";
  import DetailHeader from "./DetailHeader.svelte";
  import StatusBar from "./StatusBar.svelte";
  import RepVacations from "./RepVacations.svelte";
  import RepInformation from "./RepInformation.svelte";
  import Events from "./Events.svelte";
  import OrderSummary from "./OrderSummary.svelte";
  import OrderErrors from "./OrderErrors.svelte";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import type { CartProduct } from "@shared/types/ProductLine";
  import type { Order, SharedOrder } from "@shared/types/Order";
  import type { RetailerSalesRepContact, SalesRep } from "@shared/types/SalesRep";
  import List from "@shared/svelte/Components/Order/Messages/List.svelte";
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";
  import { showOrderMessaging, OrderMessagingType } from "@retailer/services/show_order_messaging";
  import { updateManualActionRequired } from "../Shared/OrderHelpers";
  import RetailerDistributorStore from "@retailer/svelte/stores/RetailerDistributorStore";
  import type { RetailerType } from "@shared/types/Retailer";
  import { getStockSiteLocation } from "@shared/services/retailer_user_authentication_service";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";

  import { VT_802_SPIRITS_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

  export let order: SharedOrder;
  export let retailer: RetailerType;
  export let services;

  let retailerAuth;

  $: {
    retailerAuth = $RetailerUserAuthenticationsStore?.find(
      a => a.distributor_id == order.distributor.id,
    );
  }

  let callbacks = {
    showIntegratedDeliveryDate: () => false,
  };

  let successCallback = (updatedContact: RetailerSalesRepContact) => {
    let idx = order.sales_reps.findIndex(sales_rep => sales_rep.id == updatedContact.sales_rep_id);
    order.sales_reps[idx] = {
      ...order.sales_reps[idx],
      name: updatedContact.full_name,
      contact_phone: updatedContact.contact_phone,
      contact_email: updatedContact.contact_email,
      last_order_email: null,
      last_order_text: null,
      has_phone_notification_errors: false,
      has_email_notification_errors: false,
      retailer_sales_rep_contact: updatedContact,
    };
  };

  const addRepToOrder = (salesRep: SalesRep) => {
    order.sales_reps = [...order.sales_reps, salesRep];
  };

  const removeRepFromOrder = (salesRep: SalesRep) => {
    order.sales_reps = [...order.sales_reps.filter(sr => sr.id !== salesRep.id)];
  };

  const markEventsSeen = async function () {
    const options = {
      method: "PUT",
      url: `/api/retailer/orders/${order.id}/see_events`,
    };

    await HttpPromise(options);
    order.unread_events = 0;
  };
  setTimeout(markEventsSeen, 1000);

  if (order.state === "pending_rejection") {
    order.state = "pending";
  }

  const updateOrder = async (updatedOrder: Order) => {
    order = (await updateManualActionRequired(updatedOrder)) as SharedOrder;
  };

  $: {
    order;
  }

  const showMessagesList = showOrderMessaging(order) === OrderMessagingType.MESSAGE_LIST;

  $: retailerDistributor = $RetailerDistributorStore.find(
    rd => rd.distributor.id === order.distributor.id,
  );
  $: orderActionRequired =
    order.manual_order_action_required && retailerDistributor?.is_aoo_enabled;

  $: hideStatusBar =
    order.distributor.id === VT_802_SPIRITS_DISTRIBUTOR_ID && order.warehouse_summaries.length > 1;
</script>

<FizzStyles>
  <DetailHeader {order} />
  {#if !hideStatusBar}
    <StatusBar {order} {retailer} on:orderUpdated={e => updateOrder(e.detail.order)} />
  {/if}
  <div
    class="grid-container margin-bottom--small"
    id="order_{order.id}"
    data-order-id={order.id}
    class:margin-top--small={!orderActionRequired}
  >
    {#if order.state == "pending" && !order.distributor.integrated}
      <OrderErrors
        bind:salesReps={order.sales_reps}
        {order}
        {successCallback}
        {addRepToOrder}
        {removeRepFromOrder}
      />
    {/if}
    <div class="grid-row">
      <div class="grid-col c12-12 c5-12--md c4-12--lg float-left">
        <RepVacations {order} SalesRep={services.SalesRep} />

        <RepInformation {order} />
        {#if showMessagesList}
          <div class="fizz-hide-on-small">
            <List
              orderId={order.id}
              messageThreadId={order.message_thread_id}
              extraClasses={"fizz-stack-16"}
            />
          </div>
        {/if}

        <div class="show--md">
          <Events {order} OrderEvent={services.OrderEvent} />
        </div>

        {#if retailerAuth}
          <div class="craft fizz-stack-16">
            <Alert title="Need to cancel or modify your order?">
              {#await getStockSiteLocation(retailerAuth?.distributor_id, retailerAuth?.account_number) then data}
                Contact {data.name || ""}
                {#if data.storePhone}
                  at {data.storePhone}
                {/if}
              {/await}
            </Alert>
          </div>
        {/if}
      </div>

      <div class="grid-col c7-12--md c8-12--lg">
        <ScheduledDelivery {order} retailerMode={true} {callbacks} />
        <OrderSummary {order} {services} />
      </div>
    </div>
  </div>
</FizzStyles>
